"use client";

import React, { useCallback, useEffect, useRef } from "react";
import { GENRE_PACK_CLAMP_PX } from "./pack-collapse.shared";
import { clampReleased, frameBatched, useOverflowsBlock } from "./use-overflows-block";
import styles from "./genre-pack.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * GP12 — "on small mobile screens, a pack whose logos are taller than 475px is cut to 475px, with a
 * Show More / Show Less button to expand and collapse it." the client-boundary argument.
 */

/* Anything the browser will hand focus to. Same set `nav-shell.client.tsx:338` uses for its own
   focus sweep. `summary` is defensive, not observed: today the only caller is `genre-pack.tsx` and
   the clamped region holds two `LogoGrid`s, which render none. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input, select, textarea, summary, [tabindex]:not([tabindex="-1"])';

export interface PackCollapseProps {
  /**
   * The Show More / Show Less control, built on the server as `<Disclosure
   * className={styles.showMore} hideChevron summary={<ShowMoreToggle />} />`. Two things it has to
   * satisfy, both invisible from here.
   */
  toggleSlot: React.ReactNode;
  /** The clamped region — the pack's logo grids. */
  children: React.ReactNode;
  /** The server's answer from `packProvablyFits`; absent or false leaves it to the measurement. */
  fits?: boolean;
  /** Layout only. */
  className?: string;
}

function PackCollapseImpl({ toggleSlot, children, fits = false, className }: PackCollapseProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  /* Takes the clipped tiles out of the tab order, and puts the clip box back
     to the top. Both halves of the defect in the header, and both have to run
     again every time the clip line could have moved. */
  const syncClipped = useCallback(() => {
    const body = bodyRef.current;
    const root = rootRef.current;
    if (!body || !root) {
      return;
    }

    /* Nothing is clipped while the clamp is off — released at 735px up, the pack fits, or it is
       expanded — so give every cell back without reading a rect. */
    const clamped = !clampReleased() &&
      root.getAttribute("data-cl-fits") === "false" &&
      !root.querySelector<HTMLDetailsElement>(":scope > details")?.open;
    if (!clamped) {
      body.querySelectorAll("[inert]").forEach(element => element.removeAttribute("inert"));
      return;
    }

    const bodyRect = body.getBoundingClientRect();
    /* An unlaid-out element — display:none, or jsdom, which lays nothing out —
       measures 0 on every rect, so `top >= bottom` would be `0 >= 0` and inert
       EVERY tile. Same guard, same reason, as `use-overflows-block.ts:38`. */
    if (bodyRect.height === 0) {
      return;
    }

    /* `Array.from`, not a spread: iterating a NodeList needs the dom.iterable
       lib, which this tsconfig only gets from a test-only @types package. */
    const controls = Array.from(body.querySelectorAll<HTMLElement>(FOCUSABLE));
    /* Both the cell and the control, each judged on its own rect — and the pair is not redundant. -
       The cell, because a tile's ⓘ badge is a second focus stop beside its logo and marking the
       `<li>` covers both at once. */
    const targets = new Set<HTMLElement>(controls);
    for (const control of controls) {
      const cell = control.closest<HTMLElement>("li");
      if (cell && body.contains(cell)) {
        targets.add(cell);
      }
    }

    /* Read all, then write all — two loops over the same `targets` set, not one interleaved pass. */
    const measured = Array.from(targets, element => ({
      element,
      /* ENTIRELY below the line. `bottom > bodyRect.bottom` would also take a
         half-visible tile, which is a control the reader can see and cannot
         reach — a new defect rather than a fix. */
      clipped: element.getBoundingClientRect().top >= bodyRect.bottom
    }));

    for (const { element, clipped } of measured) {
      /* `toggleAttribute`, never the jsx `inert` prop: React 18 renders `inert={false}` as
         `inert="false"`, which is still inert, and React 19 does not. */
      element.toggleAttribute("inert", clipped);
    }

    /* The clip box is scrollable by script even though `overflow: hidden` leaves the reader no
       scrollbar, wheel or touch gesture to undo it with. */
    if (body.scrollTop !== 0 && !body.contains(document.activeElement)) {
      body.scrollTop = 0;
    }
  }, []);

  /* No dependency array, deliberately. */
  useEffect(syncClipped);

  /* The measurement only feeds `data-cl-fits`, so it is written here rather than held as state. */
  const report = useCallback(
    (overflows: boolean) => {
      rootRef.current?.setAttribute("data-cl-fits", overflows ? "false" : "true");
      syncClipped();
    },
    [syncClipped]
  );
  const measure = useOverflowsBlock(bodyRef, GENRE_PACK_CLAMP_PX, report);

  /* React rewrites the attribute when the server answer changes (locals arriving after load), so
     the measurement has to be asked again. The hook already measured on mount. */
  const mounted = useRef(false);
  useEffect(() => {
    if (mounted.current) {
      measure();
    }
    mounted.current = true;
  }, [fits, measure]);

  /* The residual 15px scroll. */
  const handleFocusOut = useCallback(
    (event: FocusEvent) => {
      const body = bodyRef.current;
      const related = event.relatedTarget as Node | null;
      if (body && related && body.contains(related)) {
        return;
      }
      syncClipped();
    },
    [syncClipped]
  );

  useEffect(() => {
    const root = rootRef.current;
    const body = bodyRef.current;

    /* Capture, because `toggle` does not bubble — the same reason and the same idiom as
       `use-overflows-block.ts:62`. Without this the pass never re-runs on expand, and every tile
       stays inert inside an open pack. */
    root?.addEventListener("toggle", syncClipped, true);
    /* A logo arriving reflows the tiles inside a box whose own size is pinned by the clamp, so
       nothing else reports it (`use-overflows-block.ts` makes the same argument for its own
       measurement). */
    const onReflow = frameBatched(syncClipped);
    body?.addEventListener("load", onReflow.schedule, true);
    /* The clamp is also released by the 735px tier, which changes no attribute
       and fires no toggle. */
    window.addEventListener("resize", onReflow.schedule);
    /* A scroll moves every rect at once, so a set computed before it is stale in both directions. */
    body?.addEventListener("scroll", onReflow.schedule, { passive: true });
    /* No capture — `focusout`, unlike `toggle`, bubbles on its own, so a listener on `body` sees
       every descendant losing focus without one. */
    body?.addEventListener("focusout", handleFocusOut);

    return () => {
      root?.removeEventListener("toggle", syncClipped, true);
      body?.removeEventListener("load", onReflow.schedule, true);
      onReflow.cancel();
      window.removeEventListener("resize", onReflow.schedule);
      body?.removeEventListener("scroll", onReflow.schedule);
      body?.removeEventListener("focusout", handleFocusOut);
    };
  }, [syncClipped, handleFocusOut]);

  return (
    <div
      className={[styles.collapse, className].filter(Boolean).join(" ")}
      data-cl-fits={fits ? "true" : "false"}
      ref={rootRef}
    >
      {toggleSlot}
      <div className={styles.collapseBody} ref={bodyRef}>
        {children}
      </div>
    </div>
  );
}
export const PackCollapse = withSafeRender(PackCollapseImpl, "PackCollapse");

export default PackCollapse;
